import { apiSlice } from "@/redux/rtk/features/apiSlice/apiSlice";
import { toastHandler } from "@/utils/functions";
import queryGenerator from "@/utils/queryGenarator";

export const leadsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLead: builder.query({
      query: (id) => ({
        url: `lead/${id}`,
      }),
      providesTags: ["Lead"],
    }),
    getLeads: builder.query({
      query: (id) => ({
        url: `lead?query=all`,
      }),
      providesTags: ["LeadAll"],
    }),
    getLeadsPaginated: builder.query({
      query: (arg) => {
        const query = queryGenerator(arg);
        return {
          url: `lead?${query}`,
        };
      },
      providesTags: ["Leads"],
    }),

    addLead: builder.mutation({
      query: (values) => ({
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `lead`,
        body: values,
      }),

      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Lead added successfully", "success");
        } catch (err) {
          // Extract error message from API response
          const errorMessage = err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),

    deleteLead: builder.mutation({
      query: (arg) => {
        const id = typeof arg === "object" ? arg.id : arg;
        return {
          url: `lead/${id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json;charset=UTF-8",
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Lead deleted successfully", "warning");
        } catch (err) {
          // Extract error message from API response
          const errorMessage = err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Lead", id: arg.id },
        "Leads",
        "LeadsAll",
        "Deal", // Invalidate Deal cache as well since Deal contains Lead data
        "Deals",
        "DealAll",
      ],
    }),

    updateLead: builder.mutation({
      query: ({ id, values }) => ({
        url: `lead/${id}`,
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Lead updated successfully", "success");
        } catch (err) {
          // Extract error message from API response
          const errorMessage = err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Lead", id: arg.id },
        "Leads",
        "LeadsAll",
        "Deal", // Invalidate Deal cache as well since Deal contains Lead data
        "Deals",
        "DealAll",
      ],
    }),

    convertLead: builder.mutation({
      query: ({ data, arg, id }) => {
        const query = queryGenerator(arg, false);
        return {
          url: `lead/${id}?${query}`,
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json;charset=UTF-8",
          },
          body: data,
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Lead Converted  successfully", "success");
        } catch (err) {
          // Extract error message from API response
          const errorMessage = err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Lead", id: arg.id },
        "Leads",
        "LeadsAll",
        "Deal", // Invalidate Deal cache as well since Deal contains Lead data
        "Deals",
        "DealAll",
      ],
    }),

    bulkUpdateLead: builder.mutation({
      query: (values) => ({
        url: `lead/bulk-update`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Leads updated successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),

    bulkConvertLead: builder.mutation({
      query: (values) => ({
        url: `lead/bulk-convert`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Leads converted successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead", "Deal", "Deals", "DealAll"],
    }),

    bulkUpdateLeadStatus: builder.mutation({
      query: (values) => ({
        url: `lead/bulk-update-status`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Leads status updated successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),

    bulkUpdateLeadOwner: builder.mutation({
      query: (values) => ({
        url: `lead/bulk-update-owner`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Leads owner updated successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),
    bulkUpdateLeadDetails: builder.mutation({
      query: (values) => ({
        url: `lead/bulk-update-details`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Leads updated successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),
    getLeadSuggestions: builder.query({
      query: ({ field, key }) => ({
        url: `lead?query=suggestions&field=${field}&key=${key}`,
      }),
    }),
    syncGoogleSheet: builder.mutation({
      query: (values) => ({
        url: `lead/sync-google-sheet`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values || {},
      }),
      invalidatesTags: ["Leads", "LeadsAll", "Lead"],
    }),
  }),
});

export const {
  useGetLeadQuery,
  useGetLeadsQuery,
  useGetLeadsPaginatedQuery,
  useAddLeadMutation,
  useDeleteLeadMutation,
  useUpdateLeadMutation,
  useConvertLeadMutation,
  useBulkUpdateLeadMutation,
  useBulkConvertLeadMutation,
  useBulkUpdateLeadStatusMutation,
  useBulkUpdateLeadOwnerMutation,
  useBulkUpdateLeadDetailsMutation,
  useGetLeadSuggestionsQuery,
  useSyncGoogleSheetMutation,
} = leadsApi;
